import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';

/**
 * Button — every clickable action in the app uses this. Variants and
 * sizes match DreamsPOS template's `.btn .btn-primary / .btn-light`
 * patterns one-to-one.
 *
 * Variants:
 *   primary   — solid primary blue
 *   secondary — solid secondary orange
 *   success / danger / warning — semantic
 *   light     — neutral light
 *   outline   — bordered, transparent fill
 *   ghost     — no border, hover-tint
 *
 * Sizes:
 *   sm  — 32pt (icon-only buttons in tables, etc.)
 *   md  — 38pt (default for forms, modals)
 *   lg  — 44pt (touch-friendly)
 *   xl  — 48pt (primary CTAs in POS)
 *   pos — 56pt (huge POS action buttons like "Place an Order")
 */
type Variant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'outline' | 'ghost';
type Size = 'sm' | 'md' | 'lg' | 'xl' | 'pos';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

const SIZE: Record<Size, string> = {
  sm:  'h-8 px-3 text-12 rounded-md',
  md:  'h-9 px-4 text-13 rounded-md',
  lg:  'h-11 px-5 text-14 rounded-lg',
  xl:  'h-12 px-6 text-15 rounded-lg',
  pos: 'h-touch-lg px-7 text-16 rounded-xl',
};

const VARIANT: Record<Variant, string> = {
  primary:   'bg-primary text-[#FFFFFF] hover:bg-primary-hover disabled:bg-gray-300',
  secondary: 'bg-secondary text-[#FFFFFF] hover:bg-secondary-hover disabled:bg-gray-300',
  success:   'bg-success text-[#FFFFFF] hover:bg-success-hover disabled:bg-gray-300',
  danger:    'bg-danger text-[#FFFFFF] hover:bg-danger-hover disabled:bg-gray-300',
  warning:   'bg-warning text-[#FFFFFF] hover:bg-warning-hover disabled:bg-gray-300',
  info:      'bg-info text-[#FFFFFF] hover:bg-info-hover disabled:bg-gray-300',
  light:     'bg-light text-heading hover:bg-light-hover border border-border',
  outline:   'bg-transparent text-primary border border-primary hover:bg-primary hover:text-[#FFFFFF]',
  ghost:     'bg-transparent text-heading hover:bg-gray-100 disabled:text-gray-400',
};

export default forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'primary', size = 'md', fullWidth, leadingIcon, trailingIcon, className = '', children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={[
        'inline-flex items-center justify-center gap-2 font-medium',
        'transition-colors disabled:cursor-not-allowed',
        SIZE[size],
        VARIANT[variant],
        fullWidth ? 'w-full' : '',
        className,
      ].join(' ')}
      {...rest}
    >
      {leadingIcon}
      {children !== undefined && <span>{children}</span>}
      {trailingIcon}
    </button>
  );
});
